import type { PageContext } from './context.ts';
import { h, replace } from '../utils/dom.ts';
import { getLocale, t, type MessageKey } from '../i18n/index.ts';
import { errorBox, skeleton, spinner, toast } from '../components/ui.ts';
import { iconEl, serviceIcon, snipBadge, type IconName } from '../components/illustrations.ts';
import { poleBand, shopHero, shopInfo } from '../components/shop-hero.ts';
import { availableSlots, barbersForService, bookableDates, bookingWindow, pickBarber, type Slot } from '../utils/slots.ts';
import { formatDateKey, formatDateTime, formatDuration, formatPrice, formatTime, localized } from '../utils/format.ts';
import { dateKeyInTz, minutesInTz, type DateKey } from '../utils/time.ts';
import { contactSchema, newBookingSchema, type Booking, type ContactInput } from '../utils/validation.ts';
import type { BusyInterval } from '../services/data.ts';
import { addBookingRef, forgetContact, getSavedContact, saveContact } from '../services/device.ts';
import { errorMessageKey } from '../services/errors.ts';
import { downloadIcs } from '../utils/ics.ts';
import { tenantAsset } from '../services/tenant.ts';

interface State {
  serviceId: string | null;
  barberId: string; // "any" or a barber id
  date: DateKey | null;
  slot: Slot | null;
  bookings: BusyInterval[];
  loaded: boolean;
}

/**
 * Booking flow on one screen: service → (barber) → date → time → details.
 * Returning clients (saved contact) need just: service, time, "Book now".
 */
export function bookingPage(ctx: PageContext): HTMLElement {
  const { business, repo } = ctx;
  const locale = getLocale();
  const tz = business.timezone;
  const state: State = { serviceId: null, barberId: 'any', date: null, slot: null, bookings: [], loaded: false };

  const servicesEl = h('div', { class: 'service-list', role: 'radiogroup', 'aria-label': t('book.service') });
  const barbersEl = h('div', { class: 'chips', role: 'radiogroup', 'aria-label': t('book.barber') });
  const datesEl = h('div', { class: 'date-strip', role: 'radiogroup', 'aria-label': t('book.date') });
  const slotsEl = h('div', { class: 'slots', 'aria-live': 'polite' });
  const formEl = h('div');
  /** Numbered step with a heading ("1  Wähle deine Leistung"). */
  const step = (n: number, title: string, ...children: Node[]) =>
    h('section', { class: 'section step' },
      h('h2', { class: 'step-title' }, h('span', { class: 'step-num', 'aria-hidden': 'true' }, String(n)), title),
      ...children,
    );
  const servicesStep = step(1, t('step.service'), servicesEl);
  const pickerEl = h('div', { class: 'picker', hidden: true },
    step(2, t('step.barber'), barbersEl),
    step(3, t('step.date'), datesEl),
    step(4, t('step.time'), slotsEl),
  );
  const root = h('div', { class: 'page page-booking' },
    shopHero(business, () => servicesStep.scrollIntoView({ behavior: 'smooth', block: 'start' })),
    poleBand(),
    servicesStep,
    pickerEl,
    formEl,
    // Address, hours and contact after the booking flow: booking comes first on a phone.
    h('section', { class: 'section shop-info' }, shopInfo(business)),
  );

  const slotsFor = (date: DateKey) =>
    state.serviceId
      ? availableSlots({ business, date, serviceId: state.serviceId, barberId: state.barberId, bookings: state.bookings, now: new Date() })
      : [];

  // --- render functions ------------------------------------------------------

  function renderServices(): void {
    replace(servicesEl, business.services.map((s) => {
      const selected = s.id === state.serviceId;
      const desc = localized(s, 'description', locale);
      const card = h('button', {
        type: 'button',
        role: 'radio',
        'aria-checked': String(selected),
        class: `service-card${selected ? ' selected' : ''}`,
        'data-testid': `service-${s.id}`,
        onClick: () => selectService(s.id),
      },
        h('span', { class: 'service-icon', 'aria-hidden': 'true' }, iconEl(serviceIcon(s), 26)),
        h('span', { class: 'service-main' },
          h('strong', null, localized(s, 'name', locale)),
          desc && h('small', { class: 'muted' }, desc),
          h('small', { class: 'service-duration' }, iconEl('clock', 14), formatDuration(s.duration_min, locale)),
        ),
        h('span', { class: 'service-price' }, formatPrice(s.price, locale, business.currency)),
        h('span', { class: 'service-check', 'aria-hidden': 'true' }, iconEl('check', 16)),
      );
      if (s.color) card.style.setProperty('--service-color', s.color);
      return card;
    }));
  }

  function renderBarbers(): void {
    if (!state.serviceId) return;
    const barbers = barbersForService(business, state.serviceId);
    const chip = (id: string, label: string, sub?: string, image?: string) =>
      h('button', {
        type: 'button',
        role: 'radio',
        'aria-checked': String(state.barberId === id),
        class: `chip${state.barberId === id ? ' selected' : ''}`,
        'data-testid': `barber-${id}`,
        onClick: () => selectBarber(id),
      },
        image
          ? h('img', { src: tenantAsset(business.slug, image), alt: '', width: 36, height: 36, class: 'avatar', loading: 'lazy' })
          : h('span', { class: 'avatar avatar-initial', 'aria-hidden': 'true' }, id === 'any' ? iconEl('users', 18) : label.slice(0, 1)),
        h('span', null, label, sub ? h('small', { class: 'muted' }, sub) : null),
      );
    replace(barbersEl,
      barbers.length > 1 ? chip('any', t('book.anyBarber'), t('book.anyBarberHint')) : null,
      barbers.map((b) => chip(b.id, b.name, undefined, b.image)),
    );
  }

  function renderDates(): void {
    const dates = bookableDates(business, new Date());
    replace(datesEl, dates.map((d) => {
      const full = slotsFor(d).length === 0;
      const selected = d === state.date;
      return h('button', {
        type: 'button',
        role: 'radio',
        'aria-checked': String(selected),
        class: `date-chip${selected ? ' selected' : ''}${full ? ' full' : ''}`,
        disabled: full,
        'data-date': d,
        onClick: () => selectDate(d),
      },
        h('small', null, formatDateKey(d, locale, { weekday: 'short' })),
        h('strong', null, formatDateKey(d, locale, { day: 'numeric' })),
        h('small', null, formatDateKey(d, locale, { month: 'short' })),
      );
    }));
    datesEl.querySelector('.selected')?.scrollIntoView({ block: 'nearest', inline: 'center' });
  }

  function renderSlots(): void {
    if (!state.loaded) {
      replace(slotsEl, skeleton(8, 'chip'));
      return;
    }
    const slots = state.date ? slotsFor(state.date) : [];
    if (slots.length === 0) {
      replace(slotsEl, h('p', { class: 'empty' }, t('book.noSlots')));
      return;
    }
    // Grouped by part of the day: easier to scan than one long grid.
    const groups: { key: 'slots.morning' | 'slots.afternoon' | 'slots.evening'; icon: IconName; slots: Slot[] }[] = [
      { key: 'slots.morning', icon: 'sun', slots: [] },
      { key: 'slots.afternoon', icon: 'sunset', slots: [] },
      { key: 'slots.evening', icon: 'moon', slots: [] },
    ];
    for (const slot of slots) {
      const m = minutesInTz(slot.start, tz);
      groups[m < 12 * 60 ? 0 : m < 17 * 60 ? 1 : 2]!.slots.push(slot);
    }
    replace(slotsEl, groups.filter((g) => g.slots.length).map((g) =>
      h('div', { class: 'slot-group' },
        h('h3', { class: 'slot-group-title' }, iconEl(g.icon, 16), t(g.key)),
        h('div', { class: 'slot-grid' }, g.slots.map((slot) => {
          const selected = state.slot?.start.getTime() === slot.start.getTime();
          return h('button', {
            type: 'button',
            class: `slot${selected ? ' selected' : ''}`,
            'aria-pressed': String(selected),
            'data-testid': 'slot',
            onClick: () => selectSlot(slot),
          }, formatTime(slot.start, locale, tz));
        })),
      )));
  }

  function renderForm(): void {
    const service = business.services.find((s) => s.id === state.serviceId);
    const slot = state.slot;
    if (!service || !slot || !state.date) {
      replace(formEl);
      return;
    }
    const barberId = state.barberId === 'any' ? pickBarber(slot, state.bookings, state.date, tz) : state.barberId;
    const barber = business.barbers.find((b) => b.id === barberId)!;
    const saved = getSavedContact();

    const field = (name: keyof ContactInput, label: string, type: string, autocomplete: string, required: boolean) =>
      h('label', { class: 'field' },
        h('span', null, label),
        h('input', {
          name, type, autocomplete, required,
          value: saved?.[name] ?? '',
          maxLength: name === 'client_email' ? 254 : 100,
          inputMode: type === 'tel' ? 'tel' : undefined,
          'aria-describedby': `${name}-error`,
        }),
        h('small', { class: 'field-error', id: `${name}-error` }),
      );

    const submit = h('button', { type: 'submit', class: 'btn btn-primary btn-block', 'data-testid': 'submit' }, t('book.confirm'));
    const form = h('form', { class: 'booking-form', novalidate: true, onSubmit: (e: Event) => void onSubmit(e, form, submit, barberId) },
      ticket(localized(service, 'name', locale), barber.name, formatDateTime(slot.start, locale, tz), `${formatDuration(service.duration_min, locale)} · ${formatPrice(service.price, locale, business.currency)}`, serviceIcon(service)),
      field('client_name', t('book.name'), 'text', 'name', true),
      field('client_phone', t('book.phone'), 'tel', 'tel', true),
      field('client_email', t('book.email'), 'email', 'email', false),
      h('label', { class: 'check' },
        h('input', { type: 'checkbox', name: 'remember', checked: saved !== null }),
        h('span', null, t('book.remember')),
      ),
      h('p', { class: 'muted small' },
        t('book.consent'), ' ', t('book.cancelHint', { hours: business.rules.cancellation_hours }), ' ',
        h('a', { href: `/s/${business.slug}/datenschutz`, 'data-link': true }, t('book.privacyLink')),
      ),
      submit,
    );
    replace(formEl, step(5, t('step.details'), form));
  }

  /** Appointment summary styled as a barbershop ticket with a "cut here" line. */
  function ticket(serviceName: string, barberName: string, when: string, detail: string, icon: IconName): HTMLElement {
    return h('div', { class: 'ticket' },
      h('div', { class: 'ticket-head' },
        h('span', { class: 'ticket-icon' }, iconEl(icon, 22)),
        h('div', null, h('small', null, t('book.ticket')), h('strong', null, t('book.summary', { service: serviceName, barber: barberName }))),
      ),
      h('div', { class: 'ticket-cut', 'aria-hidden': 'true' }, iconEl('scissors', 16)),
      h('div', { class: 'ticket-body' }, h('span', { class: 'ticket-when' }, iconEl('calendar', 16), when), h('span', { class: 'muted' }, detail)),
    );
  }

  // --- actions -----------------------------------------------------------------

  function selectService(id: string): void {
    state.serviceId = id;
    state.slot = null;
    if (state.barberId !== 'any' && !barbersForService(business, id).some((b) => b.id === state.barberId)) state.barberId = 'any';
    if (barbersForService(business, id).length === 1) state.barberId = barbersForService(business, id)[0]!.id;
    pickerEl.hidden = false;
    renderServices();
    renderBarbers();
    chooseFirstAvailableDate();
    renderForm();
    pickerEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function selectBarber(id: string): void {
    state.barberId = id;
    state.slot = null;
    renderBarbers();
    chooseFirstAvailableDate();
    renderForm();
  }

  function selectDate(d: DateKey): void {
    state.date = d;
    state.slot = null;
    renderDates();
    renderSlots();
    renderForm();
  }

  function selectSlot(slot: Slot): void {
    state.slot = slot;
    renderSlots();
    renderForm();
    formEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function chooseFirstAvailableDate(): void {
    const dates = bookableDates(business, new Date());
    if (!state.date || slotsFor(state.date).length === 0) {
      state.date = dates.find((d) => slotsFor(d).length > 0) ?? dates[0] ?? null;
    }
    renderDates();
    renderSlots();
  }

  async function onSubmit(e: Event, form: HTMLFormElement, submit: HTMLButtonElement, barberId: string): Promise<void> {
    e.preventDefault();
    const data = Object.fromEntries(new FormData(form)) as Record<string, string>;
    const contact: ContactInput = { client_name: data.client_name ?? '', client_phone: data.client_phone ?? '', client_email: data.client_email ?? '' };

    form.querySelectorAll('.field-error').forEach((el) => (el.textContent = ''));
    form.querySelectorAll('[aria-invalid]').forEach((el) => el.removeAttribute('aria-invalid'));
    const parsed = contactSchema.safeParse(contact);
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        const name = String(issue.path[0]);
        const input = form.querySelector<HTMLInputElement>(`[name="${name}"]`);
        input?.setAttribute('aria-invalid', 'true');
        const err = form.querySelector(`#${name}-error`);
        if (err && !err.textContent) err.textContent = t(`error.${issue.message}` as MessageKey);
      }
      form.querySelector<HTMLInputElement>('[aria-invalid]')?.focus();
      return;
    }

    submit.disabled = true;
    replace(submit, spinner(), ' ', t('book.booking'));
    try {
      const input = newBookingSchema.parse({ ...contact, service_id: state.serviceId, barber_id: barberId, starts_at: state.slot!.start.toISOString(), locale });
      const booking = await repo.createBooking(input);
      if (data.remember === 'on') saveContact(contact);
      else forgetContact();
      addBookingRef(business.slug, { id: booking.id, cancel_token: booking.cancel_token });
      showSuccess(booking);
    } catch (err) {
      const key = errorMessageKey(err);
      toast(t(key), 'error', 6000);
      submit.disabled = false;
      replace(submit, t('book.confirm'));
      if (key === 'error.slotTaken' || key === 'error.outsideWindow') {
        state.slot = null;
        await refreshBookings();
        renderForm();
      }
    }
  }

  function showSuccess(booking: Booking): void {
    const service = business.services.find((s) => s.id === booking.service_id)!;
    const barber = business.barbers.find((b) => b.id === booking.barber_id)!;
    const start = new Date(booking.starts_at);
    const when = formatDateTime(start, locale, tz);
    replace(root,
      h('div', { class: 'success card', role: 'status', 'data-testid': 'success' },
        snipBadge(),
        h('h1', { class: 'success-title' }, t('book.successTitle')),
        h('p', null, t('book.successText', { date: when })),
        ticket(localized(service, 'name', locale), barber.name, when, business.location.address, serviceIcon(service)),
        h('div', { class: 'actions' },
          h('button', {
            type: 'button',
            class: 'btn btn-secondary',
            onClick: () => downloadIcs({
              uid: booking.id,
              start,
              end: new Date(booking.ends_at),
              title: `${localized(service, 'name', locale)} · ${business.name}`,
              location: business.location.address,
              description: `${barber.name} · ${business.contact.phone}`,
            }),
          }, t('book.addCalendar')),
          h('a', { href: `/s/${business.slug}/me`, class: 'btn btn-secondary', 'data-link': true }, t('nav.my')),
          h('button', { type: 'button', class: 'btn btn-ghost', onClick: () => ctx.navigate(`/s/${business.slug}/`) }, t('book.another')),
        ),
      ),
    );
    window.scrollTo({ top: 0 });
  }

  async function refreshBookings(): Promise<void> {
    const now = new Date();
    const { to } = bookingWindow(business, now);
    state.bookings = await repo.getBusy(now, to);
    state.loaded = true;
    if (state.serviceId) chooseFirstAvailableDate();
  }

  // --- init --------------------------------------------------------------------

  renderServices();
  refreshBookings().catch((err) => {
    console.error(err);
    replace(slotsEl, errorBox(errorMessageKey(err), () => void refreshBookings()));
  });

  // Live availability: someone else books → slots update without reload.
  ctx.onDispose(repo.subscribe(() => {
    void refreshBookings().then(() => {
      if (state.slot && state.date && !slotsFor(state.date).some((s) => s.start.getTime() === state.slot!.start.getTime())) {
        state.slot = null;
        renderForm();
        toast(t('error.slotTaken'), 'error');
      }
    });
  }));

  // Keep "today" fresh if the tab stays open past midnight.
  const timer = setInterval(() => {
    if (state.date && state.date < dateKeyInTz(new Date(), tz)) void refreshBookings();
  }, 60_000);
  ctx.onDispose(() => clearInterval(timer));

  return root;
}
